import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { components } from 'react-select';
import { ALPHA_NUMERIC_REGEX } from '@client/desktop/constants/common.constants';
import OmnitureTracking from '@client/desktop/utils/OmnitureTracking';
import SearchableSelect from '../Form/MultiSelect/SearchableSelect';
import './TextInput.css';

const TextInput = ({
	errorClass = '',
	errorText = '',
	placeholderText,
	placeholder = '',
	labelText = '',
	type,
	defaultValue = '',
	onChange = () => { },
	onBlur = () => { },
	onBeforeInput = () => { },
	handleCallback = (e) => { },
	children = '',
	maxLength = 100,
	minLength = '',
	trimInput = false,
	name = '',
	inputKey = 'userInputField',
	customClass = '',
	allowPositiveNumberOnly = false,
	allowFloatingPositiveNumberOnly = false,
	allowAlphabetsAndSpaceOnly = false,
	allowAlphaNumericOnly = false,
	testId = '',
	autoComplete = 'on',
	autoFocus = false,
	showEye = true,
	showClear = true,
	showErrorToolTip = false,
	error = '',
	validations = {},
	inputMode = '',
	readonly = false,
	defaultCountryCode = '',
	fieldGroupType = '',
	onCountryCodeChange = () => { },
	countryCodes = [],
}) => {
	const defaultCountryCodes = useMemo(() => [
		{ label: '+91', value: '+91', country: 'India' },
		{ label: '+1', value: '+1', country: 'USA' },
		{ label: '+44', value: '+44', country: 'UK' },
		{ label: '+65', value: '+65', country: 'Singapore' },
	], []);

	const isPhoneNumber = fieldGroupType === 'PHONE_NUMBER';

	const initialValue = useMemo(() => isPhoneNumber && typeof defaultValue === 'object' ? defaultValue?.value || '' : defaultValue || '',
		[isPhoneNumber, defaultValue]);

	const initialCountryCode = useMemo(() => isPhoneNumber && typeof defaultValue === 'object' && defaultValue?.countryCode
		? defaultValue.countryCode : defaultCountryCode,
		[isPhoneNumber, defaultValue, defaultCountryCode]);

	const [inputValue, setInputValue] = useState(initialValue);
	const [inType, setInType] = useState(type);
	const [countryCode, setCountryCode] = useState(initialCountryCode);

	const countryCodeOptions = useMemo(() => countryCodes.length ? countryCodes : defaultCountryCodes,
		[countryCodes, defaultCountryCodes]);

	const countryCodeSelectOptions = useMemo(() => countryCodeOptions.map((code) => ({
		label: code.country ? `${code.label} (${code.country})` : code.label,
		value: code.value,
		isDefault: code.value === '+91',
	})),
		[countryCodeOptions]);

	const getCountryCodeValue = useMemo(() => countryCodeSelectOptions.find((code) => code.value === countryCode) || null,
		[countryCodeSelectOptions, countryCode]);

	const charValidationRegex = useMemo(() => /[\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF]/g,
		[]);

	const handleChange = useCallback(() => {
		setInputValue('');
		const newVal = isPhoneNumber ? { countryCode, value: '' } : '';
		handleCallback(newVal, { name });
		onChange(newVal);
	}, [isPhoneNumber, countryCode, handleCallback, onChange, name]);

	useEffect(() => {
		if (isPhoneNumber) {
			setCountryCode(initialCountryCode);
			setInputValue(initialValue);
		}
    else {
			setInputValue(defaultValue || '');
		}
	}, [isPhoneNumber, initialCountryCode, initialValue, defaultValue]);

	const handleCountryCodeChange = useCallback((selectedOption) => {
		if (!selectedOption?.value) return;
		setCountryCode(selectedOption.value);
		const data = { countryCode: selectedOption.value, value: inputValue };
		if (isPhoneNumber) {
			handleCallback(data, { name });
			onChange(data);
		}
		onCountryCodeChange?.(selectedOption.value);
	}, [inputValue, handleCallback, name, onChange, onCountryCodeChange, isPhoneNumber]);

	const CustomSingleValue = useCallback((props) => (
		<components.SingleValue {...props}>{props.data?.value || countryCode}</components.SingleValue>
	), [countryCode]);

	const handlePhoneInput = useCallback((e) => {
		if (readonly) return;
		const cleanValue = e.target.value.replace(/[^0-9]/g, '');
		if (validations?.maxLength?.value && cleanValue.length > validations.maxLength.value) return;
		setInputValue(cleanValue);
		const data = { countryCode, value: cleanValue };
		handleCallback(data, e.target);
		onChange(data);
	}, [readonly, validations, countryCode, handleCallback, onChange]);

	const setValue = useCallback((e) => {
		if (readonly) return;
		let val = e.target.value.replace(charValidationRegex, '');
		val = trimInput ? val.trim() : val;
		setInputValue(val);
		handleCallback(val, e.target);
		onChange(val);
	}, [readonly, charValidationRegex, trimInput, handleCallback, onChange]);

	const onKeyPress = useCallback((e) => {
		const key = String.fromCharCode(e.charCode || e.which);
		if ((allowPositiveNumberOnly || isPhoneNumber) && !/^[0-9]$/.test(key)) e.preventDefault();
		if (allowFloatingPositiveNumberOnly && !/^\d$|\.$/.test(key)) e.preventDefault();
		if (allowAlphabetsAndSpaceOnly && !/^[a-zA-Z ]$/.test(key)) e.preventDefault();
		if (allowAlphaNumericOnly && !ALPHA_NUMERIC_REGEX.test(key)) e.preventDefault();
	}, [allowPositiveNumberOnly, isPhoneNumber, allowFloatingPositiveNumberOnly, inputValue, allowAlphabetsAndSpaceOnly, allowAlphaNumericOnly]);

	const handleBeforeInput = useCallback((e) => {
		const exactLength = validations?.exactLength?.value;
		if ((type === 'number' && (!/^\d$/.test(e.data) || (exactLength && e.target.value.length >= exactLength))) || readonly) {
			e.preventDefault();
		}
		onBeforeInput(e);
	}, [readonly, validations, type, onBeforeInput]);

	const pwdShowHide = useCallback(() => {
		setInType((prev) => prev === 'password' ? 'text' : 'password');
		OmnitureTracking({
			event: 'ctaClick',
			click: { ctaName: 'Eye symbol', ctaPosition: window.location.pathname.replaceAll('/', '') },
		});
	}, []);

	const commonInputProps = {
		readOnly: readonly,
		'data-testid': testId,
		id: inputKey,
		onBlur,
		onBeforeInput: handleBeforeInput,
		onKeyPress,
		autoComplete,
		autoFocus: !readonly && autoFocus,
		name,
	};

	const dateValidationProps = useMemo(() => ({
		...(type === 'date' && validations.minDate && { min: validations.minDate.value }),
		...(type === 'date' && validations.maxDate && { max: validations.maxDate.value })
	}), [type, validations]);

	const errorMessage = useMemo(() => (
		(errorClass && !showErrorToolTip) || customClass === 'forexText'
			? <p data-testid={`${inputKey}Error`} className="errorText">{errorText}</p>
			: showErrorToolTip && error && <p className="errorMsg fieldError inputfieldError">{error.message || error}</p>
	), [errorClass, errorText, showErrorToolTip, error, inputKey, customClass]);

	return isPhoneNumber ? (
		<div className={classNames('inputWrap', customClass, { 'input-container-readonly': readonly }, 'phoneNumberTextInput')}>
			<label htmlFor={inputKey} className={readonly ? 'input-label-readonly' : ''}>{labelText}</label>
			<div className={`inputField phoneInputField ${errorClass} ${readonly ? 'input-wrapper-readonly' : ''}`}>
				<div className="countryCodeSelectWrapper">
					<SearchableSelect
  options={countryCodeSelectOptions}
  value={getCountryCodeValue}
  onChange={handleCountryCodeChange}
  readonly={readonly}
  name={`${name}_countryCode`}
  className="countryCodeSearchableSelect"
  asyncOptionsCallback={() => { }}
  fetchAsyncOptions={() => Promise.resolve([])}
  renderSingleValue={CustomSingleValue}
  styles={{ container: (base) => ({ ...base, width: 'auto' }), control: (base) => ({ ...base, border: 'none', background: 'transparent' }) }}
					/>
				</div>
				<div className="phoneInputSeparator" />
				<input
  {...commonInputProps}
  onChange={handlePhoneInput}
  value={inputValue}
  className={`phoneNumberField ${readonly ? 'input-readonly' : ''}`}
  placeholder={readonly ? '' : (placeholder || placeholderText || 'Enter')}
  type="tel"
  inputMode="numeric"
  maxLength={validations?.maxLength?.value || maxLength}
  minLength={validations?.minLength?.value || minLength}
				/>
				{inputValue && showClear && !readonly && (
					<span onClick={handleChange} className="clearfieldIcon sprite pushRight" aria-label="Clear phone number" />
				)}
			</div>
			{errorMessage}
			{children}
		</div>
	) : (
		<div className={classNames(customClass === 'rejectReasonWrap' ? 'textInputWrap' : 'inputWrap', customClass, { 'input-container-readonly': readonly })}>
			<label htmlFor={inputKey} className={readonly ? 'input-label-readonly' : ''}>{labelText}</label>
			<div className={`inputField ${errorClass} ${readonly ? 'input-wrapper-readonly' : ''}`}>
				<input
  {...commonInputProps}
  onChange={setValue}
  value={inputValue}
  className={`font14 latoBold ${readonly ? 'input-readonly' : ''}`}
  placeholder={placeholder || placeholderText}
  type={inType}
  inputMode={inputMode}
  maxLength={maxLength}
  minLength={minLength}
  {...dateValidationProps}
				/>
				{type === 'password' && inputValue && showEye ? (
					<span className={`password_input sprite ${inType === 'password' ? 'pwdhideIcon' : 'pwdshowIcon'}`} onClick={pwdShowHide} />
				) : inputValue && showClear && !readonly && (
					<span onClick={handleChange} className="clearfieldIcon sprite pushRight" aria-label="Clear" />
				)}
			</div>
			{errorMessage}
			{children}
		</div>
	);
};

export default TextInput;
